import React, {useEffect, useState} from 'react';
import {
    addProject,
    assignUserToProject,
    deleteProject,
    deleteUserFromProject,
    fetchProjects,
    fetchUsers
} from "../services/apiService";
import {useAuth} from "../utils/auth.context";
import '../css/ProjectsPage.css';
import {getCurrentUser} from "../services/authService";
import Translate, {t} from "../components/Translate";
import {toast} from "react-toastify";
import DeleteIcon from "../assets/img/delete.png";
import Tooltip from "../components/Tooltip";

const currentUser = getCurrentUser() ? getCurrentUser() : {language: 'en'};
const PROJECT_NAME_MAX_LENGTH = 50;


const ProjectsPage = () => {

    const {user} = useAuth();

    const [projects, setProjects] = useState([]);
    const [newProjectName, setNewProjectName] = useState('');
    const [selectedUser, setSelectedUser] = useState({});
    const [message, setMessage] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedLocales, setSelectedLocales] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [projectNameError, setProjectNameError] = useState('');


    useEffect(() => {
        fetchUsersList();
        fetchProjectsList();
    }, []);

    const fetchUsersList = async () => {
        try {
            const data = await fetchUsers();
            setUsers(data.filter(d => d.username !== user._username));
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const fetchProjectsList = async () => {
        try {
            const data = await fetchProjects();
            setProjects(data);
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleDeleteProject = async (project) => {
        try {
            setProjects(projects.filter(p => p.id !== project.id));
            await deleteProject(project.name);
            toast.info(t(currentUser.language, 'deleted'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleAddProject = async (e) => {
        e.preventDefault();
        const projectName = newProjectName.trim();
        const validationError = getProjectNameError(projectName);

        if (validationError) {
            setProjectNameError(validationError);
            return;
        }

        try {
            const newProject = await addProject(projectName, selectedLocales);
            setProjects([...projects, newProject]);
            setNewProjectName('');
            setProjectNameError('');
            setSelectedLocales([]);
            toast.success(t(currentUser.language, 'added'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const getProjectNameError = (projectName) => {
        if (!projectName) {
            return 'Project name is required';
        }

        if (projectName.length > PROJECT_NAME_MAX_LENGTH) {
            return `Project name must be ${PROJECT_NAME_MAX_LENGTH} characters or less`;
        }

        return '';
    };

    const handleProjectNameChange = (e) => {
        const value = e.target.value;
        setNewProjectName(value);
        setProjectNameError(getProjectNameError(value.trim()));
    };

    const handleAssignUserToProject = async (projectId, username) => {
        try {
            await assignUserToProject(projectId, username);

            const updatedProjects = projects.map(project => {
                if (project.name === projectId) {
                    if (project.owners == null) {
                        project.owners = []
                    }
                    const isUserAlreadyOwner = project.owners.some(o => o.username === username);
                    if (!isUserAlreadyOwner) {
                        return {
                            ...project,
                            owners: [...project.owners, {id: Math.random(), username}]
                        };
                    }
                }
                return project;
            });

            setProjects(updatedProjects);
            toast.success(t(currentUser.language, 'assigned'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const toggleDropdown = (projectId) => {
        setDropdownOpen((prev) => ({
            ...prev,
            [projectId]: !prev[projectId],
        }));
    };

    const handleUserSelectDiv = (projectId, username) => {
        setSelectedUser({...selectedUser, [projectId]: username});
        setDropdownOpen({...dropdownOpen, [projectId]: false});
    };


    const handleRemoveOwner = (projectName, ownerUsername) => {
        try {
            deleteUserFromProject(projectName, ownerUsername);
            const updatedProjects = projects.map(project => {
                if (project.name === projectName) {
                    return {
                        ...project,
                        owners: project.owners.filter(o => o.username !== ownerUsername)
                    };
                }
                return project;
            });
            setProjects(updatedProjects);
            toast.info(t(currentUser.language, 'ownerRemoved'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }

    };

    const handleLocaleChange = (e) => {
        const value = e.target.value;
        setSelectedLocales(prevLocales =>
            prevLocales.includes(value)
                ? prevLocales.filter(locale => locale !== value)
                : [...prevLocales, value]
        );
    };

    const getSortedLocales = (locales = []) => {
        return [...locales].sort((first, second) => {
            const firstLocale = first.locale || first;
            const secondLocale = second.locale || second;
            return firstLocale.localeCompare(secondLocale);
        });
    };

    return (
        <div className="projects-page">
            <div className="project-table-container">
                <h3 className="project-table-title"><Translate language={currentUser.language} text={'projectsList'}/></h3>

                <form onSubmit={handleAddProject} className="project-form project-create-panel">
                    <div className="project-form-content">
                        <div className="project-name-field">
                            <label className="project-field-label" htmlFor="new-project-name">
                                {/*<Translate language={currentUser.language} text={'projectName'}/>*/}
                            </label>
                            <div className="project-input-with-count">
                                <input
                                    id="new-project-name"
                                    type="text"
                                    placeholder={t(currentUser.language, 'newProjectName')}
                                    value={newProjectName}
                                    onChange={handleProjectNameChange}
                                    className={`project-input ${projectNameError ? 'project-input-error' : ''}`}
                                    required
                                />
                                <span className={newProjectName.trim().length > PROJECT_NAME_MAX_LENGTH ? 'project-name-count error' : 'project-name-count'}>
                                    {newProjectName.trim().length}/{PROJECT_NAME_MAX_LENGTH}
                                </span>
                            </div>
                        </div>

                        <div className="project-locales-field">
                            <div className="locale-checkboxes">
                                <label className={`locale-choice ${selectedLocales.includes('HY') ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        value="HY"
                                        checked={selectedLocales.includes('HY')}
                                        onChange={handleLocaleChange}
                                    />
                                    <span>HY</span>
                                </label>
                                <label className={`locale-choice ${selectedLocales.includes('RU') ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        value="RU"
                                        checked={selectedLocales.includes('RU')}
                                        onChange={handleLocaleChange}
                                    />
                                    <span>RU</span>
                                </label>
                                <label className={`locale-choice ${selectedLocales.includes('EN') ? 'selected' : ''}`}>
                                    <input
                                        type="checkbox"
                                        value="EN"
                                        checked={selectedLocales.includes('EN')}
                                        onChange={handleLocaleChange}
                                    />
                                    <span>EN</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <button type="submit" className="project-button" disabled={!newProjectName.trim() || !!projectNameError}>
                        <Translate language={currentUser.language} text={'addProject'}/>
                    </button>
                </form>

                {message && <p className="project-message">{message}</p>}

                <div className={'projects-table-parent'}>
                    <table className="project-table">
                        <thead>
                        <tr>
                            <th><span className="project-table-header-label"><Translate language={currentUser.language} text={'projectName'}/></span></th>
                            <th className="project-assign-column"><span className="project-table-header-label"><Translate language={currentUser.language} text={'assignUser'}/></span></th>
                            <th className="project-locales-column"><span className="project-table-header-label"><Translate language={currentUser.language} text={'locales'}/></span></th>
                            <th><span className="project-table-header-label"><Translate language={currentUser.language} text={'projectOwners'}/></span></th>
                            <th className="project-action-column"><span className="project-table-header-label"><Translate language={currentUser.language} text={'action'}/></span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {projects.map((project, index) => (
                            <tr key={index}>
                                <td>{project.name}</td>
                                <td className="project-assign-column">
                                    <div className="project-assign-controls">
                                    <div className="custom-select-wrapper">
                                        <div
                                            className={`custom-select ${dropdownOpen[project.id] ? 'open' : ''}`}
                                            onClick={() => toggleDropdown(project.id)}
                                        >
                                            <div className="select-placeholder">
                                                {selectedUser[project.id] || t(currentUser.language, 'selectUser')}
                                            </div>
                                            <div className="select-arrow">▼</div>
                                        </div>
                                        {dropdownOpen[project.id] && (
                                            <div className="select-options">
                                                {users.map((user) => (
                                                    <div
                                                        key={user.id}
                                                        className="select-option"
                                                        onClick={() => handleUserSelectDiv(project.id, user.username)}
                                                    >
                                                        {user.username}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleAssignUserToProject(project.name, selectedUser[project.id])}
                                        className="assign-button"
                                    >
                                        <Translate language={currentUser.language} text={'assign'}/>
                                    </button>
                                    </div>
                                </td>
                                <td className="project-locales-column">
                                    <div className={'projects-page-locales-parent-div'}>
                                        {project.locales != null && getSortedLocales(project.locales).map((l, idx) => (
                                            <span key={idx} className="locale-item">
                                              {(l.locale || l) + " "}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td>
                                    <div className="project-owners-list">
                                        {project.owners?.length > 0 ? project.owners.map((o, idx) => (
                                            <span key={idx} className="owner-item" title={o.username}>
                                                <span className="owner-avatar" aria-hidden="true">
                                                    {o.username?.charAt(0)?.toUpperCase()}
                                                </span>
                                                <span className="owner-name">{o.username}</span>
                                                <button
                                                    type="button"
                                                    className="remove-owner-button"
                                                    aria-label={`Remove ${o.username}`}
                                                    onClick={() => handleRemoveOwner(project.name, o.username)}
                                                >
                                                    X
                                                </button>
                                            </span>
                                        )) : (
                                            <span className="project-owners-empty">-</span>
                                        )}
                                    </div>
                                </td>
                                <td className="project-action-column">
                                    <Tooltip text={t(currentUser.language, 'delete')}>
                                        <img
                                            className="project-delete-icon"
                                            onClick={() => handleDeleteProject(project)}
                                            width={25}
                                            height={25}
                                            src={DeleteIcon}
                                            alt={t(currentUser.language, 'delete')}
                                        />
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProjectsPage;
